import { useState, useEffect, useRef } from 'react'
import { X, Info } from 'lucide-react'

interface TimeTrackingModalProps {
  open: boolean
  onClose: () => void
  onSave: (timeSpent: number, timeRemaining: number | null) => Promise<void>
  /** Initial values in hours */
  initialTimeSpent?: number
  initialTimeRemaining?: number | null
  estimatedHours?: number
  /** Optional issue key or title for context */
  issueKey?: string
}

export function parseShortStringToHours(str: string): number {
  const trimmed = str.trim()
  if (!trimmed) return 0

  // Pure number fallback
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return parseFloat(trimmed)
  }

  const tokenRegex = /(\d+(?:\.\d+)?)\s*([wdhm])/gi
  let total = 0
  let match
  let foundAny = false
  while ((match = tokenRegex.exec(trimmed)) !== null) {
    foundAny = true
    const value = parseFloat(match[1])
    const unit = match[2].toLowerCase()
    if (unit === 'w') total += value * 40 // 1 week = 40h (5 days * 8h)
    else if (unit === 'd') total += value * 8  // 1 day = 8h
    else if (unit === 'h') total += value * 1  // 1 hour
    else if (unit === 'm') total += value / 60 // 1 min
  }

  if (!foundAny) {
    const fallback = parseFloat(trimmed)
    return isNaN(fallback) ? 0 : fallback
  }

  return Math.round(total * 100) / 100
}

export function formatHoursToShortString(totalHours: number | null | undefined): string {
  if (totalHours == null || totalHours <= 0) return ''
  const weeks = Math.floor(totalHours / 40)
  let rem = totalHours - weeks * 40
  const days = Math.floor(rem / 8)
  rem = rem - days * 8
  const hours = Math.floor(rem)
  rem = rem - hours
  const minutes = Math.round(rem * 60)

  const parts: string[] = []
  if (weeks > 0) parts.push(`${weeks}w`)
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)

  return parts.length > 0 ? parts.join(' ') : '0m'
}

export function TimeTrackingModal({
  open,
  onClose,
  onSave,
  initialTimeSpent = 0,
  initialTimeRemaining = null,
}: TimeTrackingModalProps) {
  const [spentInput, setSpentInput] = useState('')
  const [remainingInput, setRemainingInput] = useState('')
  const [saving, setSaving] = useState(false)
  const spentRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      const spentStr = formatHoursToShortString(initialTimeSpent)
      const remStr =
        initialTimeRemaining != null ? formatHoursToShortString(initialTimeRemaining) : ''
      setSpentInput(spentStr)
      setRemainingInput(remStr)
      setSaving(false)
      setTimeout(() => spentRef.current?.focus(), 50)
    }
  }, [open, initialTimeSpent, initialTimeRemaining])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) {
      document.addEventListener('keydown', handleEsc)
      return () => document.removeEventListener('keydown', handleEsc)
    }
  }, [open, onClose])

  const handleSave = async () => {
    const timeSpent = parseShortStringToHours(spentInput)
    const remaining = remainingInput.trim() ? parseShortStringToHours(remainingInput) : null
    setSaving(true)
    try {
      await onSave(timeSpent, remaining)
      onClose()
    } catch {
      // error handled by parent caller
    } finally {
      setSaving(false)
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!open) return null

  const hasInput = spentInput.trim().length > 0 || remainingInput.trim().length > 0

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-xs p-4"
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className="w-full max-w-[420px] rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-900 border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-5">
          <h2 className="text-lg font-semibold text-[#172B4D] dark:text-slate-100">
            Time tracking
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-[#6B778C] hover:text-[#172B4D] dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Inputs row: 2 columns side by side */}
        <div className="grid grid-cols-2 gap-3.5">
          {/* Time spent */}
          <div>
            <label className="block text-xs font-semibold text-[#172B4D] dark:text-slate-200 mb-1.5">
              Time spent
            </label>
            <input
              ref={spentRef}
              type="text"
              value={spentInput}
              onChange={(e) => setSpentInput(e.target.value)}
              placeholder=""
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave()
              }}
              className="w-full rounded-md border border-[#DFE1E6] bg-white px-3 py-2 text-sm text-[#172B4D] outline-none focus:border-[#4C9AFF] focus:ring-2 focus:ring-[#4C9AFF]/20 transition-all dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>

          {/* Time remaining */}
          <div>
            <label className="flex items-center gap-1 text-xs font-semibold text-[#172B4D] dark:text-slate-200 mb-1.5">
              <span>Time remaining</span>
              <span
                title="Optional. The remaining time needed to finish this task."
                className="cursor-help inline-flex items-center text-[#6B778C] hover:text-[#172B4D] dark:text-slate-400"
              >
                <Info className="h-3.5 w-3.5" />
              </span>
            </label>
            <input
              type="text"
              value={remainingInput}
              onChange={(e) => setRemainingInput(e.target.value)}
              placeholder=""
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave()
              }}
              className="w-full rounded-md border border-[#DFE1E6] bg-white px-3 py-2 text-sm text-[#172B4D] outline-none focus:border-[#4C9AFF] focus:ring-2 focus:ring-[#4C9AFF]/20 transition-all dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
        </div>

        {/* Format Guidance Hint */}
        <div className="mt-4 text-xs text-[#172B4D] dark:text-slate-300">
          <p className="font-normal text-[#172B4D] dark:text-slate-200">
            Use the format: 2w 4d 6h 45m
          </p>
          <ul className="mt-2.5 space-y-1 text-[#172B4D] dark:text-slate-300 text-xs">
            <li className="flex items-center gap-2">
              <span className="text-slate-400">•</span>
              <span>w = weeks</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-slate-400">•</span>
              <span>d = days</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-slate-400">•</span>
              <span>h = hours</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-slate-400">•</span>
              <span>m = minutes</span>
            </li>
          </ul>
        </div>

        {/* Footer buttons: [Save] Cancel */}
        <div className="mt-6 flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasInput}
            className={`rounded-md px-4 py-2 text-xs font-semibold transition-all cursor-pointer ${
              hasInput
                ? 'bg-[#0052CC] hover:bg-[#0747A6] text-white shadow-xs'
                : 'bg-[#EBECF0] text-[#A5ADBA] dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed'
            }`}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md px-3 py-2 text-xs font-semibold text-[#42526E] hover:text-[#172B4D] hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
